import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeaderBar from "./HeaderBar";
import CharacterizationReview from "./CharacterizationReview";
import CharacterizationReports from "./CharacterizationReports";
import CharacterizationRevisionHistory from "./CharacterizationRevisionHistory";
import CharacterizationFiles from "./CharacterizationFiles";
import {
  InlineLoader,
  LoadingOverlay,
} from "../../../components/loader/CharacterizationLoader";
import PdfExportProgressOverlay from "../../../components/loader/PdfExportProgressOverlay";
import PdfModal from "../../../components/PdfModal";
import { ContainerViewMaterialForPDF } from "./ContainerViewMaterialForPDF";
import { useCharacterizationData } from "../core/hooks/useCharacterizationData";
import { useContainerClientInfo } from "../core/hooks/useContainerClientInfo";
import { getCharacterizationTabs, DEFAULT_ACTIVE_TAB } from "./constants/tabs";
import {
  getRegulationTypeFromContainer,
  getValidRegulationType,
  isBenneCharacterization,
} from "./constants/regulation";
import { getContainerClientRegisteredInfo } from "../../../services/container";
import { formatCharacterizationDate } from "../core/utils/utils";
import { loadCharacterizationComments } from "../core/utils/characterizationComments";
import { resolveClientRegisteredInfoRange } from "../core/utils/characterizationViewMode";
import { hasObjectSizes } from "../../../shared/utils/sizeRangeUtils";

const CharacterisationDetails = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const {
    period: initialPeriod,
    client: initialClient,
    container,
    superContainer,
    containers,
    zone,
    listSearch,
    fromAllReports,
  } = location.state || {};

  const [period, setPeriod] = useState(initialPeriod);
  const [client, setClient] = useState(initialClient);

  const handlePeriodUpdate = useCallback((updates) => {
    setPeriod((prev) => (prev ? { ...prev, ...updates } : prev));
    if (updates?.client) {
      try {
        const parsed = JSON.parse(updates.client);
        setClient((prev) =>
          prev
            ? {
                ...prev,
                name: parsed.name ?? prev.name,
                address: parsed.address ?? prev.address,
              }
            : prev,
        );
      } catch {
        // ignore invalid client JSON
      }
    }
  }, []);

  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);

  /** Regulation imposed by the container type — also drives the exports. */
  const containerRegulationType = useMemo(
    () => getRegulationTypeFromContainer(container ?? zone),
    [container, zone],
  );

  const [characterizationType, setCharacterizationType] = useState(
    containerRegulationType,
  );

  useEffect(() => {
    setCharacterizationType(containerRegulationType);
  }, [containerRegulationType]);

  const handleRegulationChange = useCallback((regulationType) => {
    setCharacterizationType(getValidRegulationType(regulationType));
  }, []);

  const [files, setFiles] = useState(period?.clientParsed?.files || []);
  const [revisionCount, setRevisionCount] = useState(0);
  const [revisionHistoryRefreshKey, setRevisionHistoryRefreshKey] = useState(0);
  const [historyId, setHistoryId] = useState(0);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    percent: 0,
    label: "",
  });
  const [pdfFile, setPdfFile] = useState([]);
  const [openModalExport, setOpenModalExport] = useState(false);
  const [selectedFilesForExport, setSelectedFilesForExport] = useState([]);
  const [initialPdfModalAction, setInitialPdfModalAction] = useState("export");
  const [characterizationComments, setCharacterizationComments] = useState([]);

  const {
    isLoading,
    characterizationInProgress,
    historyMaterial,
    materialDelivery,
    totalWasteMass,
    totalWasteVolume,
    recoverableMaterialList,
    parentCategoriesList,
    outletData,
    totalVolumeRecoverable,
    totalMassRecoverable,
    recoverableWithOutlet,
    recoverableWithNoOutlet,
    materialTotal,
    repData,
    proportion,
    sizeRangeDistribution,
    sizeRangeMatrix,
    sizeThreshold,
    totalFinesMass,
    totalFinesVolume,
    finesBelowThreshold,
    finesBelowThresholdByMaterial,
    keptVolumeByMaterialFp0,
    keptVolumeByMaterialFpCalculated,
    refetch,
    useLatestCoefficients,
    hasNewerCoefficients,
    coefficientsVersion,
    coefficientsDate,
    latestCoefficientsDate,
    onCoefficientsToggle,
    totalValueEur,
  } = useCharacterizationData({
    period,
    container,
    superContainer,
    characterizationType,
  });

  const isBenne = useMemo(
    () => isBenneCharacterization(historyMaterial),
    [historyMaterial],
  );

  const hasSizeRanges = useMemo(
    () => hasObjectSizes(sizeRangeDistribution),
    [sizeRangeDistribution],
  );

  const { clientInfo } = useContainerClientInfo({
    container,
    superContainer,
    historyMaterial,
    historyId,
    period,
  });

  useEffect(
    () => {
      let cancelled = false;

      const loadComments = async () => {
        if (!container?._id || !historyMaterial?.length) {
          if (!cancelled) setCharacterizationComments([]);
          return;
        }

        const loadedComments = await loadCharacterizationComments({
          container,
          superContainer,
          historyMaterial,
          historyId,
        });

        if (!cancelled) {
          setCharacterizationComments(loadedComments);
        }
      };

      loadComments();

      return () => {
        cancelled = true;
      };
    },
    // eslint-disable-next-line
    [container?._id, superContainer?._id, historyMaterial, historyId],
  );

  const massChartRef = useRef(null);
  const massTableRef = useRef(null);
  const outletChartRef = useRef(null);
  const outletTableRef = useRef(null);
  const volumeChartRef = useRef(null);
  const objectsChartRef = useRef(null);
  const repChartRef = useRef(null);
  const repTableRef = useRef(null);

  const pdfMassChartRef = useRef(null);
  const pdfMassTableRef = useRef(null);
  const pdfOutletChartRef = useRef(null);
  const pdfOutletTableRef = useRef(null);
  const pdfVolumeChartRef = useRef(null);
  const pdfObjectsChartRef = useRef(null);
  const pdfRepChartRef = useRef(null);
  const pdfRepTableRef = useRef(null);
  const pdfSizeChartRef = useRef(null);
  const pdfSizeMatrixRef = useRef(null);

  const tabs = useMemo(
    () =>
      getCharacterizationTabs(t).map((tab) => {
        if (tab.id === "files") {
          return { ...tab, label: `${t("Files")} (${files.length})` };
        }
        if (tab.id === "history") {
          return {
            ...tab,
            label: `${t("Revision history")} (${revisionCount})`,
          };
        }
        return tab;
      }),
    [files.length, revisionCount, t],
  );

  // Load files from backend when component mounts or historyMaterial changes
  useEffect(() => {
    const loadFiles = async () => {
      if (!container?._id || !historyMaterial || historyMaterial.length === 0) {
        return;
      }

      try {
        const range = resolveClientRegisteredInfoRange({
          period,
          historyMaterial,
          historyId: 0,
        });
        if (!range) {
          return;
        }
        const containerId =
          superContainer && superContainer._id !== undefined
            ? superContainer._id
            : container._id;
        const response = await getContainerClientRegisteredInfo(
          range,
          containerId,
        );

        if (response?.data?.client) {
          try {
            const clientData = JSON.parse(response.data.client);
            if (clientData?.files && Array.isArray(clientData.files)) {
              setFiles(clientData.files);
            } else if (!clientData?.files) {
              // If files field doesn't exist, set empty array
              setFiles([]);
            }
          } catch (parseError) {
            // If parsing fails, keep existing files
          }
        }
      } catch (error) {
        // Silently fail - files will remain as initial state
      }
    };

    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    container?._id,
    superContainer?._id,
    historyMaterial?.length,
    period?.periodMaterialStartSubfilter,
    period?.periodMaterialStopSubfilter,
    period?.start,
    period?.stop,
  ]);

  return (
    <div className="settings-page-wrapper">
      <div className="w-full">
        <HeaderBar
          date={formatCharacterizationDate(period.start)}
          client={client}
          container={container}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
          onTabChange={setActiveTab}
          isPageLoading={isLoading}
          pdfRefs={{
            massChartRef: pdfMassChartRef,
            massTableRef: pdfMassTableRef,
            outletChartRef: pdfOutletChartRef,
            outletTableRef: pdfOutletTableRef,
            volumeChartRef: pdfVolumeChartRef,
            objectsChartRef: pdfObjectsChartRef,
            repChartRef: pdfRepChartRef,
            repTableRef: pdfRepTableRef,
            sizeChartRef: pdfSizeChartRef,
            sizeMatrixRef: pdfSizeMatrixRef,
          }}
          period={period}
          superContainer={superContainer}
          containers={containers}
          zone={zone}
          historyMaterial={historyMaterial}
          totalWasteMass={totalWasteMass}
          totalMassRecoverable={totalMassRecoverable}
          totalWasteVolume={totalWasteVolume}
          totalVolumeRecoverable={totalVolumeRecoverable}
          parentCategoriesList={parentCategoriesList}
          materialDelivery={materialDelivery}
          materialTotal={materialTotal}
          outletData={outletData}
          repData={repData}
          characterizationType={characterizationType}
          isBenneCharacterization={isBenne}
          files={files}
          selectedFilesForExport={selectedFilesForExport}
          setSelectedFilesForExport={setSelectedFilesForExport}
          setIsExporting={setIsExporting}
          setExportProgress={setExportProgress}
          setPdfFile={setPdfFile}
          setOpenModalExport={setOpenModalExport}
          setInitialPdfModalAction={setInitialPdfModalAction}
          totalFinesMass={totalFinesMass}
          totalFinesVolume={totalFinesVolume}
          finesBelowThreshold={finesBelowThreshold}
          sizeThreshold={sizeThreshold}
          clientInfo={clientInfo}
          registrationClientCode={clientInfo?.registrationClientCode}
          listSearch={listSearch}
          fromAllReports={fromAllReports}
          characterizationComments={characterizationComments}
          useLatestCoefficients={useLatestCoefficients}
        />
      </div>
      <div className="settings-content-wrapper">
        {isLoading ? (
          <InlineLoader height="400px" />
        ) : (
          <>
            {}
            <div
              className={`min-w-0 ${activeTab === "review" ? "block" : "hidden"}`}
            >
              <CharacterizationReview
                period={period}
                client={client}
                container={container}
                superContainer={superContainer}
                characterizationInProgress={characterizationInProgress}
                historyMaterial={historyMaterial}
                historyId={historyId}
                setHistoryId={setHistoryId}
                containers={containers}
                proportion={proportion}
                materialDelivery={materialDelivery}
                onAfterSegmentationUpdate={() => {
                  refetch({ silent: true });
                  setRevisionHistoryRefreshKey((k) => k + 1);
                }}
                isPageLoading={isLoading}
                onCommentsChange={setCharacterizationComments}
                onPeriodUpdate={handlePeriodUpdate}
                hasSizeRanges={hasSizeRanges}
              />
            </div>
            {activeTab === "reports" && (
              <>
                <CharacterizationReports
                  zone={zone}
                  period={period}
                  clientParsed={clientInfo?.clientParsed}
                  useLatestCoefficients={useLatestCoefficients}
                  hasNewerCoefficients={hasNewerCoefficients}
                  coefficientsVersion={coefficientsVersion}
                  coefficientsDate={coefficientsDate}
                  latestCoefficientsDate={latestCoefficientsDate}
                  onCoefficientsToggle={onCoefficientsToggle}
                  coefficientsLoading={isLoading}
                  totalWeight={period?.weight ? period.weight : 0}
                  materialDelivery={materialDelivery}
                  totalWasteMass={totalWasteMass}
                  totalWasteVolume={totalWasteVolume}
                  recoverableMaterialList={recoverableMaterialList}
                  parentCategoriesList={parentCategoriesList}
                  totalVolumeRecoverable={totalVolumeRecoverable}
                  totalMassRecoverable={totalMassRecoverable}
                  recoverableWithOutlet={recoverableWithOutlet}
                  recoverableWithNoOutlet={recoverableWithNoOutlet}
                  materialTotal={materialTotal}
                  outletData={outletData}
                  repData={repData}
                  characterizationType={characterizationType}
                  containerRegulationType={containerRegulationType}
                  onRegulationChange={handleRegulationChange}
                  sizeRangeDistribution={sizeRangeDistribution}
                  sizeRangeMatrix={sizeRangeMatrix}
                  sizeThreshold={sizeThreshold}
                  totalFinesMass={totalFinesMass}
                  totalFinesVolume={totalFinesVolume}
                  finesBelowThreshold={finesBelowThreshold}
                  finesBelowThresholdByMaterial={finesBelowThresholdByMaterial}
                  keptVolumeByMaterialFp0={keptVolumeByMaterialFp0}
                  keptVolumeByMaterialFpCalculated={
                    keptVolumeByMaterialFpCalculated
                  }
                  totalValueEur={totalValueEur}
                  isBenneCharacterization={isBenne}
                  refs={{
                    massChartRef,
                    massTableRef,
                    outletChartRef,
                    outletTableRef,
                    volumeChartRef,
                    objectsChartRef,
                    repChartRef,
                    repTableRef,
                  }}
                />
                {}
                <ContainerViewMaterialForPDF
                  refs={{
                    massChartRef: pdfMassChartRef,
                    massTableRef: pdfMassTableRef,
                    outletChartRef: pdfOutletChartRef,
                    outletTableRef: pdfOutletTableRef,
                    volumeChartRef: pdfVolumeChartRef,
                    objectsChartRef: pdfObjectsChartRef,
                    repChartRef: pdfRepChartRef,
                    repTableRef: pdfRepTableRef,
                    sizeChartRef: pdfSizeChartRef,
                    sizeMatrixRef: pdfSizeMatrixRef,
                  }}
                  parentCategoriesList={parentCategoriesList}
                  totalWasteMass={totalWasteMass}
                  totalWasteVolume={totalWasteVolume}
                  materialDelivery={materialDelivery}
                  recoverableMaterialList={recoverableMaterialList}
                  totalVolumeRecoverable={totalVolumeRecoverable}
                  totalMassRecoverable={totalMassRecoverable}
                  recoverableWithOutlet={recoverableWithOutlet}
                  recoverableWithNoOutlet={recoverableWithNoOutlet}
                  materialTotal={materialTotal}
                  outletData={outletData}
                  totalWeight={period?.weight ? period.weight : 0}
                  repData={repData}
                  characterizationType={characterizationType}
                  sizeThreshold={sizeThreshold}
                  totalFinesMass={totalFinesMass}
                  totalFinesVolume={totalFinesVolume}
                  finesBelowThreshold={finesBelowThreshold}
                  sizeRangeDistribution={sizeRangeDistribution}
                  sizeRangeMatrix={sizeRangeMatrix}
                  isBenneCharacterization={isBenne}
                  renderOffscreen={true}
                />
              </>
            )}
            <div className={activeTab === "history" ? "block" : "hidden"}>
              <CharacterizationRevisionHistory
                client={client}
                container={container}
                superContainer={superContainer}
                containers={containers}
                historyMaterial={historyMaterial}
                onRevisionCountChange={setRevisionCount}
                refreshKey={revisionHistoryRefreshKey}
              />
            </div>
            <div className={activeTab === "files" ? "block" : "hidden"}>
              <CharacterizationFiles
                files={files}
                setFiles={setFiles}
                container={container}
                period={period}
                superContainer={superContainer}
                client={client}
                historyMaterial={historyMaterial}
                selectedFilesForExport={selectedFilesForExport}
                setSelectedFilesForExport={setSelectedFilesForExport}
              />
            </div>
          </>
        )}
      </div>
      {openModalExport && (
        <PdfModal
          data={{
            ...container,
            wasteName: period.wasteCategory
              ? period.wasteCategory.wasteCategoryName
              : "-",
            positioning: container?.positioning || "",
          }}
          setOpenModalExport={setOpenModalExport}
          openModalExport={openModalExport}
          setLoadScreen={setIsExporting}
          setPdfFile={setPdfFile}
          pdfFile={pdfFile}
          characterization={true}
          pdfDateTime={new Date(period?.start || period?._time || new Date())}
          initialAction={initialPdfModalAction}
        />
      )}
      {isExporting && openModalExport && (
        <LoadingOverlay className="!z-[1060]" />
      )}
      {isExporting && !openModalExport && (
        <PdfExportProgressOverlay
          percent={exportProgress.percent}
          label={exportProgress.label}
        />
      )}
    </div>
  );
};
export default CharacterisationDetails;
